import { EventEmitter, Injectable } from "@angular/core";
import { AuthSelectors, AuthState } from "@app/auth/store";
import { EntityActions, EntitySelectors } from "@app/entity/store";
import { UserManagementActions, UserManagementSelectors } from "@app/user/store";
import { Store, select } from "@ngrx/store";
import { Observable, combineLatest, forkJoin, of } from "rxjs";
import { catchError, distinctUntilChanged, filter, map, switchMap, take, tap, withLatestFrom } from "rxjs/operators";
import { EntityAddressActionPayload, EntityUserActionPayload } from "../store/actions/entity.actions";

import { MatDialog } from "@angular/material/dialog";
import { CustomerHelper } from "@app/customer/model/customer";
import { ProjectHelper } from "@app/project/model/project";
import { ProjectDbService } from "@app/project/services/project-db.service";
import { ProjectDispatchers } from "@app/project/store/dispatchers/project.dispatchers";
import { RouterSelectors } from "@app/root-store";
import { SnackbarActions } from "@app/root-store/actions";
import { LoadingState } from "@app/root-store/api-result.state";
import { RouterDispatchers } from "@app/root-store/dispatchers";
import { ClareDialogComponent } from "@app/shared/components/clare-dialog/clare-dialog.component";
import { CommonModelService } from "@app/shared/services/common-model.service";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { TSLoggingService } from "@app/shared/services/ts-logging.service";
import { UserHelper } from "@app/user/model/user";
import { UserActions } from "@app/user/store/actions";
import { UserManagementState } from "@app/user/store/user-management.state";
import { TypedAction } from "@ngrx/store/src/models";
import { entityRoutePaths } from "../entity.routes";
import { Entity } from "../model/entity";
import { EntitiesState } from "../store/entity.state";
import { EntityInfoService } from "./entityinfo.service";

@Injectable({
    providedIn: "root",
})
export class EntityService extends CommonModelService {
    loadEntities(parentId: number): Observable<EntitiesState> {
        return this.store.pipe(
            select(EntitySelectors.selectEntitiesState),
            // If we are not loading the entities, load them
            tap(entitiesState => {
                if (entitiesState.callState === LoadingState.init) {
                    this.store.dispatch(EntityActions.loadSet.request({ parentId }));
                }
            })
        );
    }

    loadAuthUserEntities$(): Observable<EntitiesState> {
        return this.store.pipe(
            select(AuthSelectors.selectAuthUserEntityId),
            // If we have an auth entity
            filter((entityId: number) => !!entityId),
            take(1),
            switchMap((entityId: number) => this.loadEntities(entityId))
        );
    }

    selectUsers() {
        return this.store.pipe(select(UserManagementSelectors.getAllUsers));
    }

    getSelectedStaff(id: number) {
        return this.store.pipe(select(UserManagementSelectors.getSelectedStaff(id)));
    }

    selectNonSelfEntities() {
        return this.store.pipe(select(EntitySelectors.getNonSelfEntities));
    }

    selectNonSelfAndChildrenEntities() {
        return this.store.pipe(select(EntitySelectors.getNonSelfAndChildrenEntities()));
    }

    selectActiveEntityStaffs() {
        return this.store.pipe(select(UserManagementSelectors.getEntityStaffs));
    }

    selectActiveEntityStaffsState() {
        return this.store.pipe(select(UserManagementSelectors.getEntityStaffState));
    }

    getOrFetchEntityStaffs() {
        return combineLatest({
            staffState: this.selectActiveEntityStaffsState(),
            currentEntity: this.store.pipe(select(EntitySelectors.getCurrentEntityId)),
        }).pipe(
            distinctUntilChanged(),
            tap(({ staffState, currentEntity }) => {
                if (staffState.callState === LoadingState.init) {
                    this.store.dispatch(UserActions.loadEntityUsers({ entityId: currentEntity }));
                }
            }),
            map(({ staffState }) => staffState)
        );
    }

    selectCurrentEntity$() {
        return this.loadAuthUserEntities$().pipe(
            filter(d => d.callState === LoadingState.loaded),
            take(1),
            switchMap(_ => this.store.pipe(select(EntitySelectors.getCurrentEntity)))
        );
    }

    selectCurrentAlulaDealerId$() {
        return this.projectDispatchers.getActiveProjectEntity$().pipe(
            switchMap(activeProjectEntity => {
                if (activeProjectEntity?.alulaAccountNumber) {
                    return of(activeProjectEntity.alulaAccountNumber);
                }

                return this.selectCurrentEntity$().pipe(map(currEntity => currEntity?.alulaAccountNumber));
            })
        );
    }

    selectCurrentEntityId$() {
        return this.store.pipe(select(EntitySelectors.getCurrentEntityId));
    }

    selectEntity$(entityId: number) {
        return this.loadAuthUserEntities$().pipe(
            take(1),
            switchMap(_ => this.store.pipe(select(EntitySelectors.getEntity(entityId))))
        );
    }

    selectEntitiesArray$() {
        return this.loadAuthUserEntities$().pipe(
            filter(entities => !!entities.result),
            take(1),
            switchMap(_ => this.store.pipe(select(EntitySelectors.selectEntitiesArray)))
        );
    }

    selectEntities$() {
        return this.loadAuthUserEntities$();
    }

    openEntityActivationDialog(config) {
        this.dialog.open(ClareDialogComponent, config);
    }

    getBaseUrl(entityId: number) {
        return `${entityRoutePaths.root}${entityRoutePaths.edit}/${entityId}/`;
    }

    triggerDeleteAction(entity: Entity) {
        // forkJoin - wait for all the Observables to complete, then return the results as an array
        let subscription;
        subscription = this.store // eslint-disable-line prefer-const
            .pipe(select(EntitySelectors.getAuthUserEntity))
            .pipe(
                map(authEntity => {
                    if (authEntity.id === entity.id) {
                        this.store.dispatch(
                            SnackbarActions.openSnackbar({
                                message: new HttpResponseHandler(null, null, "You cannot delete your own company", true),
                            })
                        );
                        return DataServiceError.handleError(authEntity)(null);
                    }
                }),
                switchMap(_ =>
                    forkJoin([
                        this.hasUsers(entity.id).pipe(
                            take(1),
                            map(hasUsers =>
                                hasUsers ? `${new CustomerHelper().dbTypeNamePlural} or ${new UserHelper().dbTypeNamePlural}` : null
                            )
                        ),
                        this.hasChildren(entity.id).pipe(
                            take(1),
                            map(hasEntities => (hasEntities ? Entity.dbTypeNamePlural : null))
                        ),
                        this.hasProjects(entity.id).pipe(
                            take(1),
                            map(hasProjects => (hasProjects ? ProjectHelper.dbTypeNamePlural : null))
                        ),
                    ]).pipe(
                        map(hasAttachments => {
                            const attachments = hasAttachments.filter(args => args !== null);

                            if (!attachments.length) {
                                this.store.dispatch(EntityActions.deleteConfirmationSet.deleteConfirmation({ entity }));
                            } else {
                                let message = `Could not delete the ${Entity.dbTypeName} because it still has `;
                                const firstAttachment = attachments.shift();

                                message += attachments.join(", ");

                                if (attachments.length) {
                                    message += " and ";
                                }

                                message += `${firstAttachment} attached to it. Transfer or delete them before continuing.`;

                                this.store.dispatch(
                                    SnackbarActions.openSnackbar({
                                        message: new HttpResponseHandler(null, null, message, true),
                                    })
                                );
                            }

                            if (subscription) {
                                subscription.unsubscribe();
                            }
                        })
                    )
                ),
                catchError(e => {
                    if (subscription) {
                        subscription.unsubscribe();
                    }
                    return of(e);
                })
            )
            .subscribe();
    }

    hasChildren(entityId: number): Observable<boolean> {
        return this.store.pipe(
            select(EntitySelectors.getChildEntitiesForId(entityId)),
            map(entities => !!entities.length),
            take(1)
        );
    }

    hasUsers(entityId: number): Observable<boolean> {
        return this.store.pipe(select(UserManagementSelectors.getCallState)).pipe(
            withLatestFrom(this.selectAuthUser()),
            filter(([callState, authUser]) => {
                if (callState === LoadingState.init) {
                    this.store.dispatch(UserActions.loadAllEntityUsers({ entityId: authUser?.entityId }));
                }

                return callState === LoadingState.loaded;
            }),
            switchMap(() =>
                this.store.pipe(
                    select(UserManagementSelectors.getAllUsersByEntityId(entityId)),
                    map(users => !!users.length),
                    take(1)
                )
            )
        );
    }

    private hasProjects(entityId: number): Observable<boolean> {
        return this.projectDbService.getProjectsByEntity$(entityId).pipe(map(projects => projects.length > 0));
    }

    updateEntityAddress(payload: EntityAddressActionPayload, callbackEmitter: EventEmitter<any>) {
        const rawBodyContent = {
            address: payload.address,
            addressType: payload.addressType,
        };

        this.tsLoggingService.logEvent(null, "entity address", "ssopgrest", "update", null, rawBodyContent);
        this.store.dispatch(EntityActions.updateEntityAddress({ ...payload, callbackEmitter }));
    }

    updateEntityUser(payload: EntityUserActionPayload, callbackEmitter: EventEmitter<any>) {
        this.store.dispatch(EntityActions.updateEntityUserSet.request({ ...payload, callbackEmitter }));
    }

    removeShippingAddress(entity: Entity) {
        // Instead of deleting the address, remove the shippingAddress association from the entity
        this.store.dispatch(EntityActions.updateSet.request({ entity: { ...entity, shippingAddress: null } }));
    }

    clearEntityState() {
        this.store.dispatch(EntityActions.clearEntityState());
    }

    clearActiveEntityStaff() {
        this.store.dispatch(UserManagementActions.UserActions.clearActiveEntityStaff());
    }

    updateAddress(
        action: EntityAddressActionPayload & TypedAction<string>,
        entity: Entity,
        addressType: "billing" | "shipping",
        closeDialog: EventEmitter<{ entity: Entity } | DataServiceError<any>>
    ) {
        if (action && (!entity[`${addressType}Address`] || action.address?.id !== entity[`${addressType}Address`])) {
            this.updateEntityAddress(
                {
                    address: action.address,
                    entity,
                    addressType,
                },
                closeDialog
            );
        } else {
            if (action) {
                this.store.dispatch(action);
            }
            closeDialog.emit({ entity });
        }
    }

    updateContact(
        action: EntityUserActionPayload & TypedAction<string>,
        entity: Entity,
        contactType: "billing" | "shipping",
        closeDialog: EventEmitter<any>
    ) {
        if (action && (!entity[`${contactType}Userinfo`] || action.user.id !== entity[`${contactType}Userinfo`])) {
            this.updateEntityUser(
                {
                    user: action.user,
                    entity,
                    userType: contactType,
                },
                closeDialog
            );
        } else {
            if (action) {
                this.store.dispatch(action);
            }
            closeDialog.emit();
        }
    }

    createEntity(entity: Entity, callbackEmitter?: EventEmitter<any>) {
        const action = EntityActions.createSet.request({ entity, callbackEmitter });
        return this.store.dispatch(action);
    }

    updateEntity(entity: Entity, callbackEmitter?: EventEmitter<any>) {
        const action = EntityActions.updateSet.request({ entity, callbackEmitter });
        return this.store.dispatch(action);
    }

    goToEditEntityTab(entityId: number, extraPath: string = "") {
        const url = this.getBaseUrl(entityId);
        this.routerDispatchers.go([url, extraPath]);
    }

    goToAddPage() {
        this.routerDispatchers.goToAdd(entityRoutePaths);
    }

    goToEditPage(id?: number) {
        this.routerDispatchers.goToEdit(entityRoutePaths, id);
    }

    goToListPage(id?: number) {
        this.routerDispatchers.goToList(entityRoutePaths);
    }

    getAuthUserEntity() {
        return this.store.pipe(select(EntitySelectors.getAuthUserEntity));
    }

    getEntityCallState() {
        return this.store.pipe(select(EntitySelectors.getEntitiesCallState));
    }

    getEntityList() {
        return this.selectEntities$().pipe(switchMap(_ => this.store.pipe(select(EntitySelectors.getEntityList))));
    }

    getUserCallState(): Observable<boolean> {
        return this.store.pipe(
            filter(state => !!state["user-management"]),
            select(UserManagementSelectors.getCallState),
            map(callState => callState === LoadingState.loaded)
        );
    }

    cancelAction(dispatch = false) {
        return this.routerDispatchers.goToList(entityRoutePaths, dispatch);
    }

    selectCurrentRoute() {
        return this.store.pipe(select(RouterSelectors.getUrl));
    }

    updateEntityToLocalStorage(entity: Entity) {
        this.store.dispatch(EntityActions.updateSet.success({ entity }));
    }

    constructor(
        private readonly routerDispatchers: RouterDispatchers,
        private readonly projectDispatchers: ProjectDispatchers,
        readonly store: Store<UserManagementState | AuthState>,
        private tsLoggingService: TSLoggingService,
        private readonly dialog: MatDialog,
        private readonly entityInfoService: EntityInfoService,
        private readonly projectDbService: ProjectDbService
    ) {
        super(store);
    }
}
